import { IUser } from '@/entities/user/model/types'
import useRequest from '@/shared/lib/use-request'
import { ConfigStore, LangStore } from '@/shared/model/store'
import { Badge, Button, Card } from '@/shared/ui'
import { ExternalLink, Link, MapPin } from 'lucide-react'
import { useEffect } from 'react'
import { IContentFilter, IPFSProfileType } from './profile'
import { cn } from '@/shared/lib'
import { UserCard } from '@/entities/user'
import { Link as RouterLink } from 'react-router-dom'

type Props = {
	profile: IUser & IPFSProfileType
	filters: IContentFilter
	setFilters: (filter: IContentFilter) => void
}

function capitalize(s) {
	return s[0].toUpperCase() + s.slice(1)
}

function ensureHttpPrefix(url: string) {
	if (!url.startsWith('http://') && !url.startsWith('https://')) {
		return 'https://' + url
	}
	return url
}

const SidebarProfile = ({ profile, filters, setFilters }: Props) => {
	const { domain } = ConfigStore()
	const { lang, langPack } = LangStore()

	const { fetch: getCategories, result } = useRequest<string[]>('getUserCats')

	const { fetch: getTags, result: tags } = useRequest<string[]>('getUserTags')

	const { fetch, result: members } = useRequest<IUser[]>('npoMembers')

	const filterHandler = (key: string, value: string) => {
		if (value === 'all') {
			setFilters((prev: IContentFilter) => ({ ...prev, [key]: '' }))
		} else {
			setFilters((prev: IContentFilter) => ({ ...prev, [key]: value }))
		}
	}

	useEffect(() => {
		if (!profile?.address) return
		getCategories({
			params: {
				domain: domain,
				user_addr: profile.address,
				locale: lang
			}
		})
		getTags({
			params: {
				domain: domain,
				user_addr: profile.address,
				locale: lang
			}
		})

		if (profile.is_npo) {
			fetch({
				params: {
					npo_addr: profile.address
				}
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [profile.address])

	return (
		<div>
			{profile.is_npo && (
				<Card className='mb-3 flex flex-col gap-2 p-4'>
					<div className='flex items-center justify-between text-[grey]'>
						<span>{langPack('npo.members')}:</span>
						<RouterLink to={`/npo/${profile.address}`}>
							<Button size={'sm'} variant={'outline'}>
								<ExternalLink size={12} />
							</Button>
						</RouterLink>
					</div>
					<div className='mt-4 flex w-full flex-col gap-2'>
						{members
							?.filter((el: IUser) => el.confirmed)
							.map((el: IUser) => (
								<div className='flex w-full items-center justify-between'>
									<UserCard user={el} length={16} />
									{el.is_admin && (
										<Badge variant={'orange'} className='h-[25px]'>
											{langPack('npo.admin')}
										</Badge>
									)}
								</div>
							))}
					</div>
				</Card>
			)}
			{(profile.location || profile.site) && (
				<Card className='mb-3 flex flex-col gap-1 p-4'>
					{profile.location && (
						<div>
							<div className='text-[grey]'>{langPack('profile.location')}:</div>
							<div className='flex flex-row items-center gap-2'>
								<MapPin size={16} />
								{profile.location}
							</div>
						</div>
					)}
					{profile.site && (
						<div className='flex flex-col'>
							<div className='text-[grey]'>{langPack('profile.site')}:</div>
							<a href={ensureHttpPrefix(profile.site)} className='flex flex-row items-center gap-2'>
								<Link size={16} />
								{ensureHttpPrefix(profile.site)}
							</a>
						</div>
					)}
				</Card>
			)}
			<Card className='mb-3 flex flex-col gap-1 p-4'>
				<div className='text-[grey]'>{langPack('profile.categories')}:</div>
				<div
					className={cn('cursor-pointer text-[14px] hover:opacity-80', filters.category === 'all' && 'font-bold')}
					onClick={() => filterHandler('category', 'all')}
				>
					{langPack('profile.all')}
				</div>
				{result?.map((el: string) => (
					<div
						key={el}
						className={cn(
							'cursor-pointer text-[14px] hover:opacity-80',
							filters.category === `${lang}:${el}` && 'font-bold'
						)}
						onClick={() => filterHandler('category', `${lang}:${el}`)}
					>
						{el}
					</div>
				))}
			</Card>
			<Card className='flex flex-col gap-1 p-4'>
				<div className='text-[grey]'>{langPack('profile.tags')}:</div>
				<div
					className={cn('cursor-pointer text-[14px] hover:opacity-80', filters.tag === 'all' && 'font-bold')}
					onClick={() => filterHandler('tag', 'all')}
				>
					{langPack('profile.all')}
				</div>
				{tags?.map((el: string) => (
					<div
						key={el}
						className={cn(
							'cursor-pointer text-[14px] hover:opacity-80',
							filters.tag === `${lang}:${el}` && 'font-bold'
						)}
						onClick={() => filterHandler('tag', `${lang}:${el}`)}
					>
						{capitalize(el.toLowerCase())}
					</div>
				))}
			</Card>
		</div>
	)
}

export default SidebarProfile
